import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ApiError, apiPost } from "../api/client";
import { useMe } from "../hooks/useMe";
import { useAppStore } from "../state/useAppStore";

/**
 * /post-login is now a transient redirect surface. We never render the
 * workspace picker — users always land on a workspace dashboard:
 *   • last-active workspace from Zustand (persisted to localStorage), if
 *     it's still in their memberships, OR
 *   • the first membership otherwise.
 *
 * The TopBar dropdown handles workspace switching afterwards. Only the
 * empty state renders if the user truly has zero workspaces.
 */
export function PostLoginPage() {
  const { data: me, error, isLoading } = useMe();
  const navigate = useNavigate();
  const lastActiveWorkspaceId = useAppStore((s) => s.activeWorkspaceId);

  useEffect(() => {
    if (error instanceof ApiError && error.status === 401) {
      window.location.assign("/login");
    }
  }, [error]);

  useEffect(() => {
    if (!me || me.memberships.length === 0) return;
    const lastValid = me.memberships.find(
      (m) => m.workspaceId === lastActiveWorkspaceId,
    );
    const target = lastValid ?? me.memberships[0]!;
    navigate(`/workspaces/${target.workspaceId}/dashboard`, { replace: true });
  }, [me, lastActiveWorkspaceId, navigate]);

  if (isLoading) {
    return (
      <main className="min-h-screen bg-bg-canvas p-8 font-geist">
        <div className="mx-auto max-w-3xl">
          <div className="h-8 w-48 animate-pulse rounded bg-bg-soft" />
          <div className="mt-3 h-4 w-72 animate-pulse rounded bg-bg-soft" />
          <div className="mt-8 h-32 animate-pulse rounded-lg bg-bg-soft" />
        </div>
      </main>
    );
  }

  if (error && !(error instanceof ApiError && error.status === 401)) {
    return (
      <main className="min-h-screen bg-bg-canvas p-8 text-danger font-geist">
        <div className="mx-auto max-w-3xl">{error.message}</div>
      </main>
    );
  }

  if (!me) return null;

  // Only the empty-state ever renders. Any user with ≥1 workspace is
  // immediately redirected by the effect above.
  if (me.memberships.length === 0) {
    return <EmptyStateRedeem displayName={me.user.displayName} email={me.user.email} />;
  }

  // Brief loading flash while the redirect effect runs.
  return null;
}

/**
 * Empty-state UI for users who signed in via OIDC but have no workspace
 * memberships. Surfaces the Phase A onboarding paths in priority order:
 *   1. Have an invitation code? → redeem → provision a workspace
 *   2. Otherwise → static message (until Path B's self-serve plan picker)
 *
 * Path B (plan picker + billing) is intentionally not yet here — that's
 * the explicit "open the gates" business decision per the platform-gaps
 * spec sequencing.
 */
function EmptyStateRedeem({
  displayName,
  email,
}: {
  displayName: string;
  email: string;
}) {
  const [code, setCode] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");

  const redeem = useMutation<
    { workspaceId: string; workspaceName: string },
    ApiError,
    void
  >({
    mutationFn: () =>
      apiPost("/api/onboarding/redeem-code", {
        code: code.trim(),
        ...(workspaceName.trim() && { workspace_name: workspaceName.trim() }),
      }),
    onSuccess: (data) => {
      // Refresh and route into the new workspace.
      window.location.assign(`/workspaces/${data.workspaceId}/dashboard`);
    },
  });

  return (
    <main className="min-h-screen bg-bg-canvas p-8 font-geist">
      <div className="mx-auto max-w-2xl">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-3">
            Signed in as
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text">
            <span className="text-accent">{displayName}</span>
          </h1>
          <p className="mt-1 text-sm text-text-2">{email}</p>
        </header>

        <section className="rounded-lg border border-border p-6">
          <h2 className="text-base font-semibold text-text">
            Have an invitation code?
          </h2>
          <p className="mt-1 text-sm text-text-2">
            Paste the code your Xamun contact gave you. Your workspace will be
            provisioned and you'll be signed straight in.
          </p>

          <label className="mt-4 block text-sm">
            <span className="font-medium">Code</span>
            <input
              className="mt-2 w-full rounded-md border border-border bg-bg-soft p-2 text-sm font-mono"
              placeholder="abc123-XYZ"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
          </label>

          <label className="mt-3 block text-sm">
            <span className="font-medium">Workspace name</span>{" "}
            <span className="text-text-3">(optional — defaults to the code's preset)</span>
            <input
              className="mt-2 w-full rounded-md border border-border bg-bg-soft p-2 text-sm"
              placeholder="ACME · COO"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
            />
          </label>

          {redeem.error && (
            <div className="mt-3 rounded-md bg-danger-soft p-3 text-sm text-danger">
              {redeem.error.status === 404
                ? "Code not found. Double-check it with your contact."
                : redeem.error.status === 410
                ? "This code has expired or been revoked."
                : redeem.error.status === 429
                ? "This code has reached its submission cap."
                : redeem.error.status === 409
                ? "You already belong to a workspace — try signing out and back in."
                : `Could not redeem (${redeem.error.status}).`}
            </div>
          )}

          <button
            type="button"
            className="mt-4 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            disabled={code.trim().length < 4 || redeem.isPending}
            onClick={() => redeem.mutate()}
          >
            {redeem.isPending ? "Redeeming…" : "Redeem code"}
          </button>
        </section>

        <p className="mt-6 rounded-lg border border-dashed border-border p-4 text-sm text-text-3">
          No code? PlayStudio is in invite-only testing. Reach out to your Xamun
          contact, or request access via the form your team's been sharing.
        </p>
      </div>
    </main>
  );
}
